import { useState, useEffect, useCallback, useRef } from "react";
import { formatInputToCapitalization, currencyFormate, currencyInputFormate, removeCurrencyComma } from "../Date_Formate_Utilitiy/Index";
import { flushSync } from "react-dom";
import { useDispatch } from "react-redux";
import { openModal } from "../../redux/actions/action-creator";
import { fieldConfig } from "../../pages/gatewayoftally/master/create/fieldNextPrev";

export const useInputActionLedger = ({
  data,
  setData,
  validations,
  refs,
  check_dropdown,
  inputRefs,
  effectiveSlug,
  stateRef,
}) => {
  const [submitConfirm, setSubmitConfirm] = useState("");

  const focusField = useCallback((fieldName, direction = "next") => {
    if (!fieldName) {
      if (direction === "next") setSubmitConfirm("Accept");
      return;
    }

    // Use a small timeout to let React finish rendering dynamic fields
    setTimeout(() => {
      const field = fieldConfig[fieldName];
      // Use stateRef.current to avoid stale closures
      const latestData = stateRef?.current || data;
      const navigationData = effectiveSlug ? { ...latestData, group_slug: effectiveSlug } : latestData;
      const ref = inputRefs[fieldName];

      // Check if the field is visible/accessible based on refs and inputRefs
      if (ref && ref.current) {
        ref.current.focus();
        if (ref.current.setSelectionRange && (ref.current.type === "text" || ref.current.type === "number")) {
          ref.current.setSelectionRange(0, ref.current.value?.length || 0);
        }
      } else {
        // If field is hidden, skip to next/prev in config or fallback to inputRefs order
        let fallbackField = null;
        if (field) {
          const nextTarget = typeof field[direction] === "function" ? field[direction](navigationData) : field[direction];
          fallbackField = nextTarget;
        }

        if (!fallbackField) {
          const fields = Object.keys(inputRefs);
          const currentIndex = fields.indexOf(fieldName);
          fallbackField = direction === "next" ? fields[currentIndex + 1] : fields[currentIndex - 1];
        }

        if (fallbackField && fallbackField !== fieldName) {
          focusField(fallbackField, direction);
        } else if (direction === "next") {
          setSubmitConfirm("Accept");
        }
      }
    }, 50);
  }, [effectiveSlug, inputRefs, stateRef]); // Removed 'data' from dependencies

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (e.code === "Space" || e.key === "Enter") {
      e.preventDefault();
      return;
    }

    // Apply field-specific validations
    const validation = validations[name];
    if (validation) {
      const isValid = validation(value);
      if (isValid || value === "") {
        const updatedValue =
          name === "company_name" ||
            name === "mailing_name" ||
            name === "address" ||
            name === "city" ||
            name === "name" ||
            name === "ledger_name" ||
            name === "alise_name" ||
            name === "symbol" ||
            name === "formal_name"
            ? formatInputToCapitalization(value)
            : name === "no_of_decimal_place" ||
              name === "no_of_decimal_place_for_amount"
              ? value.slice(-1)
              : value;
        setData((prevData) => ({ ...prevData, [name]: updatedValue }));
      }
    } else {
      setData((prevData) => ({ ...prevData, [name]: value }));
    }
  };
  const handleDropdownClick = (name, value) => {
    // 1. Prepare updated data ONLY for calculating the next field navigation
    // We use a functional update for the actual state to avoid overwriting 
    // concurrent updates (like group_id/group_slug from DropdownLayout_1)
    setData((prevState) => {
      const updatedData = { ...prevState, [name]: value };
      if (name === "ledger_name") {
        updatedData.mailing_name = value;
      }
      if (name === "opening_balance") {
        const parts = value.split(",");
        if (value.trim() !== "" && parts.length === 1) {
          updatedData.opening_balance = currencyFormate(value);
        }
      }
      return updatedData;
    });

    // 2. Navigation logic
    // We calculate the next field based on what we just set.
    // Note: prevState inside setData might have updated group_slug, but here 'data' is still stale.
    // However, for navigation calculation, we use the merged data.
    const latestData = stateRef?.current || data;
    const currentDataWithNewValue = { ...latestData, [name]: value };

    const field = fieldConfig[name];
    let nextField = null;

    if (field && field.next) {
      const navigationData = effectiveSlug
        ? { ...currentDataWithNewValue, group_slug: effectiveSlug }
        : currentDataWithNewValue;
      nextField =
        typeof field.next === "function"
          ? field.next(navigationData)
          : field.next;
    }

    if (!nextField) {
      const fields = Object.keys(inputRefs);
      const currentIndex = fields.indexOf(name);
      nextField = fields[currentIndex + 1] || null;
    }

    focusField(nextField, "next");
  };

  const handleFocus = (e) => {
    const { name, value } = e.target;
    const focusAction = refs[name]?.onFocus;
    if (name === 'opening_balance') {
      setSubmitConfirm("");
      const new_value = removeCurrencyComma(value);
      setData((prev) => ({ ...prev, [name]: new_value }));
    }
    if (name === 'pan_it_no') {
      setSubmitConfirm("");
    }
    if (focusAction) focusAction();
  };

  const handleBlur = (e) => {
    if (document.querySelector('.modal_link') || document.querySelector('.modal_message')) return;
    const { name, value } = e.target;
    const blurAction = refs[name]?.onBlur;
    if (name === 'opening_balance') {
      const parts = value.split(",");
      if (value.trim() !== '' && parts.length === 1) {
        const new_value = currencyFormate(value);
        setData((prev) => ({ ...prev, [name]: new_value }));
      }
    }
    if (blurAction) blurAction(value, setData);
  };

  const handleKeyDown = (e) => {
    if (document.querySelector('.modal_link') || document.querySelector('.modal_message')) {
      return;
    }
    const { name, value } = e.target;
    if (e.key === "Enter" || (e.key === "Tab" && !e.shiftKey)) {
      e.preventDefault();
      handleDropdownClick(name, value);
      return;
    }
    if (e.shiftKey && e.key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      let prevField = null;

      setSubmitConfirm("");

      const field = fieldConfig[name];
      if (field) {
        if (field.prev) {
          const navigationData = effectiveSlug ? { ...data, group_slug: effectiveSlug } : data;
          prevField =
            typeof field.prev === "function" ? field.prev(navigationData) : field.prev;
        }
      }

      if (!prevField) {
        const fields = Object.keys(inputRefs);
        const currentIndex = fields.indexOf(name);
        prevField = fields[currentIndex - 1] || null;
      }

      focusField(prevField, "prev");
    }
  };

  return {
    data,
    setData,
    handleChange,
    handleFocus,
    handleBlur,
    handleKeyDown,
    submitConfirm,
    setSubmitConfirm,
    handleDropdownClick,
  };
};
